import { FilterableColumn } from "@/components/ui/data-table";
import { COUNTRY_OPTIONS } from "../constants/countries";

// Define all the option mappings used in the application columns
const relationshipStatusOptions = [
  { value: "single", label: "Ledig" },
  { value: "married", label: "Verheiratet" }
];

const criminalRecordOptions = [
  { value: "yes", label: "Ja" },
  { value: "no", label: "Nein" }
];

const recognitionStatusOptions = [
  { value: "not_applied", label: "Nicht beantragt" },
  { value: "pending", label: "Beantragt - Ausstehend" },
  { value: "approved", label: "Anerkannt" },
  { value: "partially_approved", label: "Teilweise anerkannt" }
];

const languageSchoolOptions = [
  { value: "afu", label: "AFU GmbH" },
  { value: "goethe", label: "Goethe-Institut" },
  { value: "osd", label: "ÖSD" },
  { value: "telc", label: "telc GmbH" },
  { value: "testdaf", label: "TestDaF-Institut" },
  { value: "other", label: "Andere" }
];

const visaStatusOptions = [
  { value: "fachkraft_anerkennung", label: "Fachkraft in Anerkennung" },
  { value: "anerkannte_fachkraft", label: "Anerkannte Fachkraft" },
  { value: "hilfskraft", label: "Hilfskraft während Anerkennung" },
  { value: "ausbildung", label: "Berufsausbildung" },
  { value: "sprachkurs", label: "Sprachkurs vor Ausbildung" },
  { value: "eu_blue_card", label: "EU Blaue Karte" },
  { value: "other", label: "Andere" }
];

const workExperienceOptions = [
  { value: "0", label: "0 Jahre" },
  { value: "1", label: "1 Jahr" },
  { value: "2", label: "2 Jahre" },
  { value: "3", label: "3 Jahre" },
  { value: "more", label: "Mehr als 3 Jahre" }
];

const advancedTrainingOptions = [
  { value: "intensivpflege", label: "Intensiv- und Beatmungspflege" },
  { value: "heimleitung", label: "Heimleitung" },
  { value: "intensiv", label: "Intensiv- und Anästhesiepflege" },
  { value: "onkologie", label: "Onkologie" },
  { value: "operation", label: "Operations- und Endoskopiedienst" },
  { value: "palliativ", label: "Palliativpflege" },
  { value: "pdl", label: "Pflegedienstleitung" },
  { value: "praxisanleitung", label: "Praxisanleitung" },
  { value: "psychiatrie", label: "Psychiatrie" },
  { value: "qm", label: "Qualitätsmanagement" },
  { value: "wbl", label: "Wohnbereichsleitung" },
  { value: "wundmanagement", label: "Wundmanagement" }
];

const insuranceOptions = [
  { value: "yes", label: "Ja" },
  { value: "no", label: "Nein" },
  { value: "unknown", label: "Unbekannt" }
];

const facilityPreferencesOptions = [
  { value: "hospital", label: "Krankenhaus" },
  { value: "nursing_home", label: "Pflegeheim" },
  { value: "outpatient_care", label: "Ambulante Pflege" },
  { value: "rehabilitation", label: "Rehabilitation" },
  { value: "hospice", label: "Hospiz" },
  { value: "day_care", label: "Tagespflege" },
  { value: "other", label: "Andere" }
];

const positionPreferencesOptions = [
  { value: "nurse", label: "Pflegefachkraft" },
  { value: "nursing_assistant", label: "Pflegehilfskraft" },
  { value: "head_nurse", label: "Stationsleitung" },
  { value: "nursing_director", label: "Pflegedienstleitung" },
  { value: "quality_manager", label: "Qualitätsmanager" },
  { value: "practice_instructor", label: "Praxisanleiter" }
];

const workingHoursOptions = [
  { value: "full_time", label: "Vollzeit" },
  { value: "part_time_75", label: "Teilzeit (75%)" },
  { value: "part_time_50", label: "Teilzeit (50%)" },
  { value: "minijob", label: "Minijob" }
];

const shiftModelOptions = [
  { value: "full_time", label: "Vollzeit" },
  { value: "part_time", label: "Teilzeit" },
  { value: "minijob", label: "Minijob" }
];

const languageSkillsOptions = [
  { value: "a1", label: "A1" },
  { value: "a2", label: "A2" },
  { value: "b1", label: "B1" },
  { value: "b2", label: "B2" },
  { value: "c1", label: "C1" },
  { value: "c2", label: "C2" }
];

const driversLicenseOptions = [
  { value: "b", label: "Klasse B" },
  { value: "be", label: "Klasse BE" },
  { value: "c", label: "Klasse C" },
  { value: "ce", label: "Klasse CE" },
  { value: "d", label: "Klasse D" },
  { value: "de", label: "Klasse DE" }
];

const genderOptions = [
  { value: "male", label: "Männlich" },
  { value: "female", label: "Weiblich" },
  { value: "other", label: "Divers" }
];

const booleanOptions = [
  { value: "true", label: "Ja" },
  { value: "false", label: "Nein" }
];

// Define operators for each column type
const getTextOperators = () => [
  { value: "contains", label: "enthält", type: "text" as const },
  { value: "not_contains", label: "enthält nicht", type: "text" as const },
  { value: "equals", label: "ist gleich", type: "text" as const },
  { value: "not_equals", label: "ist nicht gleich", type: "text" as const },
  { value: "starts_with", label: "beginnt mit", type: "text" as const },
  { value: "ends_with", label: "endet mit", type: "text" as const },
  { value: "is_empty", label: "ist leer", type: "text" as const },
  { value: "is_not_empty", label: "ist nicht leer", type: "text" as const }
];

const getSelectOperators = () => [
  { value: "equals", label: "ist", type: "select" as const },
  { value: "not_equals", label: "ist nicht", type: "select" as const },
  { value: "is_empty", label: "ist leer", type: "text" as const },
  { value: "is_not_empty", label: "ist nicht leer", type: "text" as const }
];

const getDateOperators = () => [
  { value: "equals", label: "ist", type: "date" as const },
  { value: "before", label: "vor", type: "date" as const },
  { value: "after", label: "nach", type: "date" as const },
  { value: "is_empty", label: "ist leer", type: "text" as const },
  { value: "is_not_empty", label: "ist nicht leer", type: "text" as const }
];

export const applicationFilterableColumns: FilterableColumn[] = [
  {
    id: "name",
    label: "Name",
    type: "text",
    operators: getTextOperators()
  },
  {
    id: "job_title",
    label: "Stellenbezeichnung",
    type: "text",
    operators: getTextOperators()
  },
  {
    id: "qualification",
    label: "Qualifikation",
    type: "text",
    operators: getTextOperators()
  },
  {
    id: "phase",
    label: "Phase",
    type: "text",
    operators: getTextOperators()
  },
  {
    id: "status",
    label: "Status",
    type: "select",
    operators: getSelectOperators(),
    options: [
      { value: "screening", label: "Screening" },
      { value: "paused", label: "Pausiert" },
      { value: "hired", label: "Eingestellt" },
      { value: "invited_to_interview", label: "Interview-Einladung" },
      { value: "passed_interview", label: "Interview bestanden" },
      { value: "failed_interview", label: "Interview nicht bestanden" },
      { value: "declined", label: "Abgelehnt" }
    ]
  },
  {
    id: "expected_start",
    label: "Erwarteter Start",
    type: "date",
    operators: getDateOperators()
  },
  {
    id: "gender",
    label: "Geschlecht",
    type: "select",
    operators: getSelectOperators(),
    options: genderOptions
  },
  {
    id: "date_of_birth",
    label: "Geburtsdatum",
    type: "date",
    operators: getDateOperators()
  },
  {
    id: "nationality",
    label: "Nationalität",
    type: "select",
    operators: getSelectOperators(),
    options: COUNTRY_OPTIONS
  },
  {
    id: "country",
    label: "Land",
    type: "select",
    operators: getSelectOperators(),
    options: COUNTRY_OPTIONS
  },
  {
    id: "city",
    label: "Stadt",
    type: "text",
    operators: getTextOperators()
  },
  {
    id: "state",
    label: "Bundesland",
    type: "text",
    operators: getTextOperators()
  },
  {
    id: "postal_code",
    label: "PLZ",
    type: "text",
    operators: getTextOperators()
  },
  {
    id: "relationship_status",
    label: "Beziehungsstatus",
    type: "select",
    operators: getSelectOperators(),
    options: relationshipStatusOptions
  },
  {
    id: "criminal_record",
    label: "Vorbestraft",
    type: "select",
    operators: getSelectOperators(),
    options: criminalRecordOptions
  },
  {
    id: "nursing_education_status",
    label: "Pflegeausbildung Status",
    type: "select",
    operators: getSelectOperators(),
    options: [
      { value: "completed", label: "Abgeschlossen" },
      { value: "in_progress", label: "In Bearbeitung" },
      { value: "not_started", label: "Nicht begonnen" }
    ]
  },
  {
    id: "estimated_completion_date",
    label: "Voraussichtlicher Abschluss",
    type: "date",
    operators: getDateOperators()
  },
  {
    id: "education_location",
    label: "Ausbildungsort",
    type: "text",
    operators: getTextOperators()
  },
  {
    id: "recognition_status",
    label: "Berufsanerkennung",
    type: "select",
    operators: getSelectOperators(),
    options: recognitionStatusOptions
  },
  {
    id: "german_language_skills",
    label: "Deutschkenntnisse",
    type: "select",
    operators: getSelectOperators(),
    options: languageSkillsOptions
  },
  {
    id: "language_school",
    label: "Sprachschule",
    type: "select",
    operators: getSelectOperators(),
    options: languageSchoolOptions
  },
  {
    id: "german_drivers_license",
    label: "Deutscher Führerschein",
    type: "multiselect",
    operators: [
      { value: "contains", label: "enthält", type: "multiselect" as const },
      {
        value: "not_contains",
        label: "enthält nicht",
        type: "multiselect" as const
      },
      { value: "is_empty", label: "ist leer", type: "text" as const },
      { value: "is_not_empty", label: "ist nicht leer", type: "text" as const }
    ],
    options: driversLicenseOptions
  },
  {
    id: "visa_application_status",
    label: "Visum Status",
    type: "select",
    operators: getSelectOperators(),
    options: [
      { value: "not_applied", label: "Nicht beantragt" },
      { value: "applied", label: "Beantragt" },
      { value: "granted", label: "Erteilt" }
    ]
  },
  {
    id: "visa_status",
    label: "Visum Typ",
    type: "select",
    operators: getSelectOperators(),
    options: visaStatusOptions
  },
  {
    id: "family_reunion",
    label: "Familiennachzug",
    type: "select",
    operators: getSelectOperators(),
    options: booleanOptions
  },
  {
    id: "visa_validity",
    label: "Visum gültig bis",
    type: "date",
    operators: getDateOperators()
  },
  {
    id: "work_experience",
    label: "Berufserfahrung",
    type: "select",
    operators: getSelectOperators(),
    options: workExperienceOptions
  },
  {
    id: "advanced_training",
    label: "Weiterbildungen",
    type: "multiselect",
    operators: [
      { value: "contains", label: "enthält", type: "multiselect" as const },
      {
        value: "not_contains",
        label: "enthält nicht",
        type: "multiselect" as const
      },
      { value: "is_empty", label: "ist leer", type: "text" as const },
      { value: "is_not_empty", label: "ist nicht leer", type: "text" as const }
    ],
    options: advancedTrainingOptions
  },
  {
    id: "health_insurance",
    label: "Krankenversicherung",
    type: "select",
    operators: getSelectOperators(),
    options: insuranceOptions
  },
  {
    id: "travel_health_insurance",
    label: "Reisekrankenversicherung",
    type: "select",
    operators: getSelectOperators(),
    options: insuranceOptions
  },
  {
    id: "facility_preferences",
    label: "Einrichtungspräferenzen",
    type: "multiselect",
    operators: [
      { value: "contains", label: "enthält", type: "multiselect" as const },
      {
        value: "not_contains",
        label: "enthält nicht",
        type: "multiselect" as const
      },
      { value: "is_empty", label: "ist leer", type: "text" as const },
      { value: "is_not_empty", label: "ist nicht leer", type: "text" as const }
    ],
    options: facilityPreferencesOptions
  },
  {
    id: "position_preferences",
    label: "Positionspräferenzen",
    type: "multiselect",
    operators: [
      { value: "contains", label: "enthält", type: "multiselect" as const },
      {
        value: "not_contains",
        label: "enthält nicht",
        type: "multiselect" as const
      },
      { value: "is_empty", label: "ist leer", type: "text" as const },
      { value: "is_not_empty", label: "ist nicht leer", type: "text" as const }
    ],
    options: positionPreferencesOptions
  },
  {
    id: "working_hours",
    label: "Arbeitszeit",
    type: "select",
    operators: getSelectOperators(),
    options: workingHoursOptions
  },
  {
    id: "shift_model",
    label: "Schichtmodell",
    type: "select",
    operators: getSelectOperators(),
    options: shiftModelOptions
  },
  {
    id: "earliest_start_date",
    label: "Frühester Starttermin",
    type: "date",
    operators: getDateOperators()
  },
  {
    id: "created_at",
    label: "Erstellt am",
    type: "date",
    operators: getDateOperators()
  },
  {
    id: "updated_at",
    label: "Aktualisiert am",
    type: "date",
    operators: getDateOperators()
  }
];
